import { Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { CreditPlan, CreditPlanInterface } from 'src/app/model/credit-plan';
import { ColumnDef } from 'src/app/model/column-definition';
import { CreditPlanService } from 'src/app/services/credit-plan/credit-plan.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';

@Component({
  selector: 'app-credit-plan-page',
  templateUrl: './credit-plan-page.component.html',
  styleUrl: './credit-plan-page.component.scss'
})
export class CreditPlanPageComponent {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, {static: true}) sort: MatSort = new MatSort();

  /** The table of creditPlans */
  @ViewChild(MatTable) table!: MatTable<CreditPlan>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<CreditPlan>([]);

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new suppliers */
  private _sub: Subscription | null = null;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    {header: "Plan Name", field: "name"},
    {header: "Active", field: "active"},
    {header: "APR", field: "apr", format: "percent"},
    {header: "Duration", field: "duration"},
    {header: "MDR", field: "mdr", format: "percent"},
    {header: "Supplier", field: "supplier"},
    {header: "Short Plan Description (Notes)", field: "notes"},
    {header: "Long Plan Description (Description)", field: "description"},
    {header: "Actions", field: "actions", format: "actions"},
  ]
  public displayedCols: string[] = this.colDefs.map(x => x.field);

  /**
   * Constructor for the creditPlan page to initialize services and set a base snapshot 
   * 
   * @param _creditPlanService 
   */
  constructor(
    private _creditPlanService: CreditPlanService,
    private _router: Router,
    private _dialogService: DialogService,
  ) {
    // Subscribe to timer changes
    this._creditPlanService.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });
  }

  /**
   * Initialize the sort, paginator, and creditPlan data source
   */
  ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimieters
    this.source.filterPredicate = (data: CreditPlan, filter: string): boolean => {
      return data.toString(this.colDefs).includes(filter);
    }

    // Get the creditPlans from the service
    this._sub = this._creditPlanService.creditPlans$.subscribe(
      (resp: CreditPlan[]) => {
        this._populateSource(resp);
      });

    this._populateSource(this._creditPlanService.getCreditPlans());
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: CreditPlan, property: string): string | number => {
      return (item as any)[property];
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._sub?.unsubscribe();
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Edit a credit plan
   */
  public editCreditPlan(p: any): void {
    const plan = p as CreditPlan;

    const id = plan.id;
    this._dialogService.openEditCreditPlan(plan).subscribe(
      (updated: {intf: CreditPlanInterface, updateNotes: string} | undefined): void => {
        if (updated == undefined) return;

        this._creditPlanService.updateCreditPlan(id, updated.intf, updated.updateNotes);
      });
  }

  /**
   * Create a new credit plan
   */
  public createCreditPlan(): void {
    this._dialogService.openCreateCreditPlan().subscribe(
      (created: CreditPlanInterface | undefined): void => {
        if (created == undefined) return;

        this._creditPlanService.createCreditPlan(created);
      });
  }

  /**
   * Populate the data source meticulously so it doesn't lag the creditPlan
   * Note: I don't know if async is required, I'm just paranoid
   */
  private async _populateSource(incoming: CreditPlan[]): Promise<void> {
    // Don't update the table when the user is not on the page => angular will be mad :(
    // TODO: can add this clause to updating the source data. Can then add notifications to some future service from changes found here
    if (this._router.url.replace("/", "") !== AppConstants.MENU_CREDIT_PLANS) return;

    let source = this.source.data;
    const current = this.source.data.map((x: CreditPlan) => x.copyOf());

    // For each incoming creditPlan, determine if it exists in the source. 
    const update = async () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If creditPlan IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the creditPlans are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf();
            }

            // We've updated the creditPlan, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    await update();

    // Add new creditPlans that we don't currently have
    const add = async () => {
      for (let newCreditPlan of incoming) {
        source.push(newCreditPlan);
      }
    }
    await add();

    // Remove old creditPlans that have been removed
    const remove = async () => {
      for (let oldCreditPlan of current) {
        source = source.filter(x => x.id !== oldCreditPlan.id);
      }
    }
    await remove();

    // Update the table
    this.source.data = source;
  }
}